import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

type ToastType = 'info' | 'success' | 'warning' | 'error';

interface Toast {
   id: string;
   message: string;
   type: ToastType;
}

interface ToastContextValue {
   showToast: (message: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast() {
   const context = useContext(ToastContext);
   if (!context) {
      throw new Error('useToast must be used within a ToastProvider');
   }
   return context;
}

const typeStyles: Record<ToastType, string> = {
   info: 'bg-blue-600 text-white',
   success: 'bg-green-600 text-white',
   warning: 'bg-yellow-500 text-black',
   error: 'bg-red-600 text-white',
};

const typeIcons: Record<ToastType, string> = {
   info: 'ℹ️',
   success: '✓',
   warning: '⚠',
   error: '✕',
};

interface ToastProviderProps {
   children: React.ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
   const [toasts, setToasts] = useState<Toast[]>([]);
   const timersRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

   const removeToast = useCallback((id: string) => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
      const timer = timersRef.current.get(id);
      if (timer) {
         clearTimeout(timer);
         timersRef.current.delete(id);
      }
   }, []);

   const showToast = useCallback(
      (message: string, type: ToastType = 'info') => {
         const id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
         setToasts((prev) => [...prev, { id, message, type }]);

         const timer = setTimeout(() => {
            removeToast(id);
         }, 4000);
         timersRef.current.set(id, timer);
      },
      [removeToast],
   );

   useEffect(() => {
      return () => {
         timersRef.current.forEach((timer) => clearTimeout(timer));
      };
   }, []);

   return (
      <ToastContext.Provider value={{ showToast }}>
         {children}
         <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2">
            <AnimatePresence>
               {toasts.map((toast) => (
                  <motion.div
                     key={toast.id}
                     initial={{ opacity: 0, y: 20, scale: 0.95 }}
                     animate={{ opacity: 1, y: 0, scale: 1 }}
                     exit={{ opacity: 0, y: -10, scale: 0.95 }}
                     transition={{ duration: 0.2 }}
                     className={`flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg ${typeStyles[toast.type]}`}
                  >
                     <span className="text-sm font-medium">{typeIcons[toast.type]}</span>
                     <span className="text-sm">{toast.message}</span>
                     <button
                        onClick={() => removeToast(toast.id)}
                        className="ml-2 opacity-70 hover:opacity-100"
                     >
                        ✕
                     </button>
                  </motion.div>
               ))}
            </AnimatePresence>
         </div>
      </ToastContext.Provider>
   );
}
